import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import CartFillWidget from "./cart/CartFillWidget";
import CartLineItem from "./cart/CartLineItem";
import CartEmptyState from "./cart/CartEmptyState";

// The nudge appears from £19 (the 12-pouch box) up to the £25 free-delivery line.
// Worked in pence, because 12 x £19/12 adds up to 18.999… in floating point.
const NUDGE_FROM_PENCE = 1900;
const FREE_DELIVERY_PENCE = 2500;

const resolveStatus = (totalCount, fillLevel) => {
  if (totalCount === 0) return "Empty pouch";
  if (fillLevel >= 100) return "Ready for checkout";
  return "Filling with krunch";
};

export default function CartDrawer() {
  const {
    open,
    closeCart,
    detailed,
    subtotal,
    updateQty,
    removeItem,
    fillLevel,
    totalCount,
    isFreeDelivery,
    activeTierLabel,
    totalSavings,
    checkout,
  } = useCart();

  const { isLoggedIn, user } = useAuth();
  const showRewardsBanner = !isLoggedIn && !user;

  const status = resolveStatus(totalCount, fillLevel);
  const subtotalPence = Math.round(subtotal * 100);
  const showDeliveryNudge = subtotalPence >= NUDGE_FROM_PENCE && subtotalPence < FREE_DELIVERY_PENCE;
  const remainingForFreeDelivery = ((FREE_DELIVERY_PENCE - subtotalPence) / 100).toFixed(2);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            className="drawer-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
            data-testid="cart-drawer-backdrop"
          />
          <motion.aside
            key="drawer"
            className="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            data-testid="cart-drawer"
          >
            <header className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-white/10">
              <div>
                <div className="product-no">Your Krunch</div>
                <h3 className="font-display text-2xl mt-1 text-cream">Cart</h3>
              </div>
              <button
                onClick={closeCart}
                className="text-cream hover:text-gold transition-colors"
                aria-label="Close cart"
                data-testid="cart-drawer-close"
              >
                <X size={22} />
              </button>
            </header>

            <CartFillWidget fillLevel={fillLevel} status={status} />

            {/* Active Tier Deal Banner */}
            {totalCount > 1 && (
              <div className="px-6 py-2.5 bg-gold/15 border-b border-gold/30 flex items-center justify-between text-xs">
                <span className="font-display tracking-wider text-gold uppercase">{activeTierLabel}</span>
                {totalSavings > 0 && (
                  <span className="text-cream font-bold">Saved £{totalSavings.toFixed(2)}</span>
                )}
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {detailed.length === 0 ? (
                <CartEmptyState />
              ) : (
                <>
                {showRewardsBanner && totalCount > 0 && (
                  <Link
                    to="/account"
                    onClick={closeCart}
                    className="block mx-5 mb-4 p-3 bg-cream/10 border border-cream/20 rounded-lg text-center hover:bg-cream/20 transition-colors"
                    data-testid="cart-rewards-banner"
                  >
                    <span className="text-cream text-sm font-display tracking-wide">
                      ✨ Sign in for rewards & exclusive perks
                    </span>
                  </Link>
                )}
                <ul className="space-y-5">
                  {detailed.map((it) => (
                    <CartLineItem
                      key={it.id}
                      item={it}
                      onRemove={() => removeItem(it.id)}
                      onIncr={() => updateQty(it.id, it.qty + 1)}
                      onDecr={() => updateQty(it.id, it.qty - 1)}
                    />
                  ))}
                </ul>
                </>
              )}
            </div>

            {totalCount > 0 && totalCount < 12 && (
              <div className="mx-5 mb-4 p-4 bg-[#F6A81E]/10 border border-[#F6A81E]/30 rounded-xl text-center">
                <p className="text-[#F6A81E] text-sm font-bold tracking-wide">
                  {totalCount === 4 && "Add 2 more pouches to get them for just £1 each!"}
                  {totalCount === 5 && "Add 1 more pouch of your choice for FREE!"}
                  {totalCount === 10 && "Add 2 more pouches to unlock The Krunch 12+ discount!"}
                  {/* Fallback for other amounts under 12, optional but good for UX */}
                  {![4, 5, 10].includes(totalCount) && totalCount < 6 && `Add ${6 - totalCount} more for The Krunch 6 deal!`}
                  {![4, 5, 10].includes(totalCount) && totalCount > 6 && `Add ${12 - totalCount} more for The Krunch 12+ deal!`}
                </p>
              </div>
            )}

            <footer className="border-t border-white/10 px-6 pt-5 pb-6 space-y-4">
              {showDeliveryNudge && (
                <p
                  className="px-4 py-3 rounded-xl bg-gold/10 border border-gold/30 text-cream text-sm text-center"
                  role="status"
                  data-testid="cart-delivery-nudge"
                >
                  Almost there! Add just <span className="font-bold text-gold">£{remainingForFreeDelivery}</span> of krunch
                  to unlock free UK delivery.
                </p>
              )}
              {isFreeDelivery && totalCount > 0 && (
                <p
                  className="px-4 py-3 rounded-xl bg-gold/15 border border-gold/40 text-gold text-sm font-bold text-center"
                  role="status"
                  data-testid="cart-delivery-unlocked"
                >
                  ✨ Free UK delivery unlocked!
                </p>
              )}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-cream">
                  <div>
                    <span className="uppercase tracking-[0.22em] text-xs block">Subtotal</span>
                    {totalSavings > 0 && (
                      <span className="text-[10px] uppercase tracking-wider text-gold font-bold">
                        Multibuy Savings Applied
                      </span>
                    )}
                  </div>

                  <div className="text-right">
                    {totalSavings > 0 && (
                      <span className="line-through text-cream/40 text-base mr-2">
                        £{(subtotal + totalSavings).toFixed(2)}
                      </span>
                    )}
                    <span className="font-display text-2xl text-gold" data-testid="cart-subtotal">
                      £{subtotal.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-[11px] text-cream/70 pt-1">
                  <span>Shipping:</span>
                  <span>{isFreeDelivery ? "FREE UK Delivery" : "Free UK delivery above £25."}</span>
                </div>
              </div>

              <button
                disabled={detailed.length === 0}
                onClick={checkout}
                className="btn-gold w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed py-4 text-base font-display tracking-wider"
                data-testid="cart-checkout-btn"
              >
                Proceed to Checkout <ArrowRight size={16} />
              </button>

              <div className="flex items-center justify-center gap-1.5 text-[11px] text-cream/50">
                <ShieldCheck size={14} />
                <span>Secure payment via Apple Pay, Google Pay & Card</span>
              </div>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}